import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// ─── User-defined app settings ────────────────────────────────────────────────
// Device-level UX preferences (not account data) persisted in localStorage.
// Designed to grow: add a key to AppSettings + DEFAULT_SETTINGS and it's
// instantly available everywhere via useSettings() — future themes, etc.

export type SizePreset = 'normal' | 'large' | 'xl';

export interface AppSettings {
  soundEnabled: boolean;
  scoreSize: SizePreset;
  arrowSize: SizePreset;
}

export const DEFAULT_SETTINGS: AppSettings = {
  soundEnabled: true,
  scoreSize: 'normal',
  arrowSize: 'normal',
};

const STORAGE_KEY = 'tripleFreezerSettings';

// ─── Size presets consumed by the game board ──────────────────────────────────

/** Score number font-size (PlayerCard) */
export const SCORE_SIZE_PX: Record<SizePreset, number> = {
  normal: 16,
  large: 24,
  xl: 34,
};

/** Dice value-arrow button: height, gap from the die, and chevron icon size (Dice) */
export const ARROW_SIZE: Record<SizePreset, { height: number; gap: number; icon: number }> = {
  normal: { height: 26, gap: 8, icon: 16 },
  large: { height: 36, gap: 16, icon: 22 },
  xl: { height: 48, gap: 26, icon: 30 },
};

export const SIZE_LABELS: Record<SizePreset, string> = {
  normal: 'Normal',
  large: 'Large',
  xl: 'XL',
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface SettingsContextValue {
  settings: AppSettings;
  setSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
  resetSettings: () => void;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  setSetting: () => {},
  resetSettings: () => {},
});

/** Read + merge with defaults so older saved blobs missing new keys never break */
const loadSettings = (): AppSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(loadSettings);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable (private mode) — settings still work for the session */
    }
  }, [settings]);

  const setSetting = useCallback(
    <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetSettings = useCallback(() => setSettings(DEFAULT_SETTINGS), []);

  return (
    <SettingsContext.Provider value={{ settings, setSetting, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
